/**
 * 登録・ログイン・パスワード再設定・アカウントの閉鎖の再入力の「フォームの属性」を 1 か所にまとめる（2026-09-27 creator/auth-forms）。
 *
 * WHY: founder がダッシュボードを触ったフィードバック（最優先）。ブラウザ（Chrome・Safari）とパスワード管理（1Password 等）の
 *   自動入力と「強力なパスワードを提案」が効かない画面があった。パスワード管理はフォームの構造と属性だけを見て判断するので、
 *   画面ごとに書くと 1 か所の書き忘れで効かなくなる。属性はここで作り、画面は広げて使う（テストは authForms.test.ts）。
 *
 * パスワード管理が見るもの（Chrome の「Sign-in form best practices」・WebKit の Password Rules の公開資料を読んだ範囲）:
 *   - 本物の <form>（method="post"）と <button type="submit">。div + onClick だと「送信された」と分からず、保存を出さない
 *   - 各 input の安定した id / name と <label for>。name が無い・毎回変わると、保存した値を次回どの欄に入れるか決められない
 *   - autocomplete: 登録 = new-password（これが無いと生成が出ない）/ ログイン = current-password。
 *     メールの欄は "username"（アカウント名として保存される。email だけだと住所録の扱いになる管理がある）。
 *     "username email" と 2 つ並べるのは HTML の決まりに反し、ブラウザは属性ごと無視する（input.autocomplete が "" になる。
 *     2026-09-27 リーダーの目視で発見）。メールのキーボードは type=email と inputMode が出す
 *   - 送信の成功後に画面が遷移する（SPA なら History API の遷移）。同じ画面のまま書き換えると「失敗した」と見なされることがある
 */

/**
 * パスワードの長さ。サーバーの規則（apps/server/src/modules/platform/routes.ts の credentials・recovery.ts の reset:
 * `z.string().min(10).max(200)`）と同じ値にする。サーバーを変えたらここも変える（画面側は往復を減らすための先回りで、正はサーバー）。
 * 10 = 開発者アカウントはゲームの課金・デプロイ・全プレイヤーデータを握るので弱いパスワードを許さない（サーバーのコメント）
 */
export const MIN_PASSWORD = 10;
export const MAX_PASSWORD = 200;

/**
 * Safari の passwordrules 属性（WebKit の Password Rules）。Safari・1Password はこれを読んで、規則に合うパスワードを生成する。
 * サーバーは長さだけを見る（文字の種類の決まりは無い）ので、長さだけを書く。
 * WHY 文字の種類（required: upper 等）を足さないか: サーバーに無い決まりを画面だけで足すと、ほかの管理（Chrome 等）が作った
 *   パスワードや手で決めたパスワードと食い違う。Safari の既定の生成（大小英字・数字・ハイフンの 20 文字）は長さの規則だけで通る
 */
export const PASSWORD_RULES = `minlength: ${MIN_PASSWORD}; maxlength: ${MAX_PASSWORD};`;

/** 画面ごとのフォームの id（パスワード管理が画面を見分ける手がかり。登録とログインを同じフォームで切り替えない） */
export const AUTH_FORM_IDS = {
  login: 'login-form',
  signup: 'signup-form',
  forgot: 'forgot-password-form',
  reset: 'reset-password-form',
  closeAccount: 'close-account-form',
  // 2026-09-27 creator/dev-oauth-ui: 再認証のダイアログ（components/Reauth.tsx・今のパスワード）と、パスワードの設定 / 変更
  // （pages/AccountLoginMethods.tsx・新しいパスワード）。閉鎖のフォームと同じ画面に出るので id を分ける
  reauth: 'reauth-form',
  setPassword: 'set-password-form'
} as const;

/**
 * <form> の属性。action はダミー（JS が送るので使われない）だが、パスワード管理の一部は action の無いフォームを
 * 「送信先の無い検索窓」のように扱うので、その画面の URL を入れておく（JS が止まっていても外部へは送らない）。
 * noValidate: 入力の誤りは画面の文言（i18n）で出す（ブラウザの吹き出しは言語が混ざる）。属性は残るのでパスワード管理は読める
 */
export function authFormProps(kind: keyof typeof AUTH_FORM_IDS, action: string) {
  return { id: AUTH_FORM_IDS[kind], method: 'post', action, noValidate: true } as const;
}

/**
 * メールアドレスの欄。
 * - autocomplete: 登録・ログインは "username"（アカウント名として保存・自動入力される）。パスワードを忘れたの欄も
 *   同じにする（保存済みのアカウント名を候補に出せる）。"username email" は無効な値で、ブラウザが無視する（上の説明）
 * - inputMode / autoCapitalize / spellCheck: スマホで @ のあるキーボード・先頭を大文字にしない・赤線を出さない
 *
 * 将来の「パスキーを自動入力の候補に出す」（WebAuthn の conditional UI = navigator.credentials.get({ mediation: 'conditional' })）について
 * （2026-09-28 creator/passkey-login。今回はボタンだけで、この欄は変えていない）:
 *   そのときはこの欄だけ autocomplete="username webauthn" にする。これは「autocomplete の値は 1 つ」の約束の **唯一の例外**。
 *   WHY 例外にしてよいか: HTML の仕様が、アカウント名の値の後ろに webauthn の印を 1 つ付ける形を正式に認めている
 *   （"username email" のような 2 つの値の並びとは違い、ブラウザが無視しない）。2026-09-28 リーダーがブラウザで
 *   input.autocomplete が "username webauthn" を保つことを確認済み。ほかの欄（パスワード等）には付けない。
 *   その時は devOAuth.test.ts の「autocomplete は値が 1 つだけ」の検査に、この 1 つだけの例外を足す。
 *   サーバー側は今の口のままで足りる（loginPasskeyRoutes.ts: allowCredentials 空・challenge は画面を開いた時に取り、5 分の期限の前に取り直す）
 */
export function emailFieldProps(id = 'email') {
  return {
    id,
    name: 'email',
    type: 'email',
    autoComplete: 'username',
    inputMode: 'email',
    autoCapitalize: 'none',
    spellCheck: false,
    maxLength: 254 // サーバーの上限（credentials の email.max(254)）
  } as const;
}

/**
 * パスワードの欄。
 * - 'new'（登録・再設定・確認の欄）: autocomplete=new-password → Chrome / Safari / 1Password の生成が出る。
 *   name も new-password にする（Chrome は name / id に "new" があると新しいパスワードの欄と判断しやすい）
 * - 'current'（ログイン・閉鎖の再入力）: autocomplete=current-password → 保存済みの値を入れる。生成は出さない
 * - confirm: 再設定の「もう一度」の欄。同じ new-password にする（生成した値を管理が両方に入れる）。id / name だけ変える
 * - id: 同じ画面にパスワードの欄が 2 つ以上あるとき（/account の閉鎖のフォーム + 再認証のダイアログ）の重複よけ
 */
export function passwordFieldProps(kind: 'new' | 'current', opts: { confirm?: boolean; id?: string } = {}) {
  const base = kind === 'new' ? (opts.confirm ? 'confirm-password' : 'new-password') : 'password';
  return {
    // id: 同じ画面に 2 つ目のフォーム（ダイアログ）が出るときだけ変える（id は画面の中で 1 つ = <label for> が迷わない）。
    //   name と autocomplete は変えない（パスワード管理が見るのはこちら。2026-09-27 creator/dev-oauth-ui）
    id: opts.id ?? base,
    name: base,
    type: 'password',
    autoComplete: kind === 'new' ? 'new-password' : 'current-password',
    // 長さの規則は新しいパスワードにだけ付ける。ログインの欄に minlength を付けると、規則ができる前の短いパスワードで
    // ブラウザが止めることがある（今は noValidate で止めないが、属性として間違った規則を管理に見せない）
    ...(kind === 'new' ? { minLength: MIN_PASSWORD, passwordrules: PASSWORD_RULES } : {}),
    maxLength: MAX_PASSWORD,
    spellCheck: false,
    autoCapitalize: 'none'
  } as const;
}

/**
 * ログイン中の人がパスワードだけを入れるフォーム（アカウントの閉鎖の再入力）に置く、見えないアカウント名の欄。
 * WHY: パスワードの欄だけのフォームだと、パスワード管理はどのアカウントの値か分からず、候補を出さないか全件を出す。
 *   Chrome の公開資料どおり autocomplete=username で値を入れた欄を置く（display:none。読み上げ・タブ移動にも出さない）
 */
export function hiddenUsernameProps(email: string) {
  return {
    type: 'text',
    name: 'username',
    autoComplete: 'username',
    value: email,
    readOnly: true,
    // hidden 属性ではなく style: styles.css の input の display 指定が hidden 属性（UA の display:none）に勝つことがあるため
    style: { display: 'none' },
    tabIndex: -1
  } as const;
}

/** 手前の画面の検査: 入れたパスワードがサーバーの長さの規則に合うか（合わなければ送らずに文言を出す） */
export function passwordLengthOk(password: string): boolean {
  return password.length >= MIN_PASSWORD && password.length <= MAX_PASSWORD;
}

/**
 * ログインの成功後に行く先。ログインしていない人が開いた URL は、App.tsx の catch-all が state.from に持ってくる
 * （出金の確認の #token も含む）。外部の URL・プロトコル相対（//evil）には行かない（開いたリダイレクトを作らない）
 */
export function afterLoginPath(from: unknown): string {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//') || from.startsWith('/\\')) return '/';
  // ログイン・登録の画面自体へは戻さない（ループになる）
  if (/^\/(login|signup)(?:[/?#]|$)/.test(from)) return '/';
  return from;
}

/**
 * パスワードの表示の切り替え（目のアイコン・2026-09-28 creator/login-redesign・指示書 4）。
 * 変えるのは type だけ（password ↔ text）。id・name・autocomplete・passwordrules・minLength は**そのまま**にする。
 * WHY: パスワード管理は autocomplete と name で欄を見分ける。表示中に属性を変えると、保存・生成・自動入力が効かなくなる
 *   （founder が Chrome で「パスワードを保存」が出るのを確かめ済み = 壊さない）。
 *   送信の直前には画面（components/PasswordField.tsx）が非表示へ戻す（type=password で送ると、どのブラウザも確実に「パスワードの送信」と見る）
 */
export function withPasswordVisibility<P extends { type: string }>(props: P, visible: boolean): Omit<P, 'type'> & { type: 'password' | 'text' } {
  return { ...props, type: visible ? 'text' : 'password' };
}
